// Library Methods
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";

// Constants
const INK = "#202338";
const INK_2 = "#5B6D7A";
const WARM = "#E2772E";
const LINE = "#5B6D7A80";

const WIDTH = 300;
const HEIGHT = 58;
const MARGIN = 10;
const AXIS_Y = 24;

type BreakBarType = {
  breakPrismDioptres: number | null;
  recoveryPrismDioptres: number | null;
  // The largest break on this card, so the two directions are drawn to one scale and can be
  // compared by eye.
  domain: number;
  // True when the ramp ran out before the picture split, so the number is a floor rather than
  // a limit.
  isLowerBound?: boolean;
};

/**
 * One direction's range: zero out to the point the picture split, with the recovery marked.
 *
 * One bar per direction rather than one shared axis with zero in the middle. The old chart put
 * convergence and divergence on opposite sides of a centre line, which reads as a single
 * quantity that can go either way -- they are two separate measurements of two separate
 * things, and each belongs beside the head figure showing what was done to the eyes.
 *
 * The zero sits above the axis, because below it is where the recovery caption goes and a short
 * bar put the two on top of each other.
 */
const BreakBar = ({
  breakPrismDioptres,
  recoveryPrismDioptres,
  domain,
  isLowerBound = false,
}: BreakBarType) => {
  const { t } = useTranslation();

  const track = WIDTH - 2 * MARGIN;
  const toX = (value: number) => MARGIN + (value / Math.max(domain, 1)) * track;

  // Keeps the recovery caption inside the canvas when the recovery sits near either end.
  const recoveryCaption =
    recoveryPrismDioptres === null
      ? null
      : t("back together {{value}}Δ", {
          value: recoveryPrismDioptres.toFixed(0),
        });
  const captionHalfWidth = (recoveryCaption?.length ?? 0) * 2.9;

  return (
    <Box
      component="svg"
      role="img"
      data-testid="synoptophore-break-bar"
      aria-label={t("break and recovery")}
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      sx={{ display: "block", width: "100%", mx: "auto" }}
    >
      <line
        x1={MARGIN}
        y1={AXIS_Y}
        x2={WIDTH - MARGIN}
        y2={AXIS_Y}
        stroke={LINE}
      />
      <text x={MARGIN} y={AXIS_Y - 9} fontSize="10" fill={INK_2}>
        0
      </text>

      {breakPrismDioptres !== null && (
        <>
          <rect
            x={MARGIN}
            y={AXIS_Y - 5}
            width={toX(breakPrismDioptres) - MARGIN}
            height={10}
            rx={2}
            fill={WARM}
          />
          <text
            x={toX(breakPrismDioptres)}
            y={AXIS_Y - 9}
            fontSize="11"
            fontWeight={600}
            fill={INK}
            textAnchor="end"
          >
            {isLowerBound ? "▶ " : ""}
            {`${breakPrismDioptres.toFixed(0)}Δ`}
          </text>
        </>
      )}

      {recoveryPrismDioptres !== null && recoveryCaption && (
        <>
          <line
            x1={toX(recoveryPrismDioptres)}
            y1={AXIS_Y - 9}
            x2={toX(recoveryPrismDioptres)}
            y2={AXIS_Y + 9}
            stroke={INK}
            strokeWidth={2}
          />
          <text
            x={Math.min(
              Math.max(toX(recoveryPrismDioptres), MARGIN + captionHalfWidth),
              WIDTH - MARGIN - captionHalfWidth
            )}
            y={AXIS_Y + 24}
            fontSize="10"
            fill={INK_2}
            textAnchor="middle"
          >
            {recoveryCaption}
          </text>
        </>
      )}
    </Box>
  );
};

export default BreakBar;
